<div class="about-page">
  <app-headerhome></app-headerhome>

  <main>
    <section class="about-hero">
      <div class="about-container about-hero-grid">
        <div class="about-hero-copy">
          <span class="about-eyebrow">{{ about.eyebrow || 'Reliable regional travel' }}</span>
          <h1>{{ about.title || 'Moving people, one honest fare at a time' }}</h1>
          <p>{{ about.intro || operatorName + ' connects people to home, work and opportunity with safe coaches, reliable departures and fares that stay fair.' }}</p>
          <div class="about-actions">
            <a class="about-btn about-btn-primary" routerLink="/" fragment="search">Book a bus</a>
            <a class="about-btn about-btn-secondary" routerLink="/contact-us">Talk to us</a>
          </div>
        </div>
        <div class="about-hero-visual">
          <img
            [src]="about.heroImage || 'assets/images/about/mash-poa-coach-road.jpeg'"
            [alt]="operatorName + ' coach travelling on the road'"
            fetchpriority="high">
          <div class="about-journey-badge">
            <img [src]="configStore.getConfig('self.logo', 'assets/images/logo.webp')" alt="">
            <span><strong>{{ about.journeyCount || '10M+' }}</strong> {{ about.journeyLabel || 'journeys booked' }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="about-stats" [attr.aria-label]="operatorName + ' in numbers'">
      <div class="about-container stats-grid">
        @for (stat of stats; track stat.label) {
          <div class="stat-cell">
            <strong>{{ stat.value }}</strong>
            <span>{{ stat.label }}</span>
          </div>
        }
      </div>
    </section>

    <section class="about-section">
      <div class="about-container story-grid">
        <div class="story-images">
          <img
            [src]="about.storyImagePrimary || 'assets/images/about/mash-poa-coach-front.jpeg'"
            [alt]="operatorName + ' Scania coach ready for departure'"
            loading="lazy">
          <img
            [src]="about.storyImageSecondary || 'assets/images/about/mash-poa-coach-nandi.jpeg'"
            [alt]="operatorName + ' Nandi coach on a regional route'"
            loading="lazy">
        </div>
        <div class="story-copy">
          <span class="section-kicker">Our story</span>
          <h2>From one route to a regional network</h2>
          <p class="section-intro">What began as a simple promise to make long-distance travel dependable has grown into a network serving communities across Kenya and East Africa.</p>
          <div class="timeline">
            @for (milestone of milestones; track milestone.year) {
              <article class="timeline-item">
                <span class="timeline-dot"></span>
                <div>
                  <small>{{ milestone.year }}</small>
                  <h3>{{ milestone.title }}</h3>
                  <p>{{ milestone.body }}</p>
                </div>
              </article>
            }
          </div>
        </div>
      </div>
    </section>

    <section class="about-section about-values">
      <div class="about-container">
        <div class="section-heading">
          <span class="section-kicker">What guides us</span>
          <h2>The standards behind every journey</h2>
        </div>
        <div class="values-grid">
          <article class="value-card value-safety">
            <span class="value-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 4 6v6c0 5 4 8.5 8 10 4-1.5 8-5 8-10V6l-8-4z"></path><path d="m9 12 2 2 4-4"></path></svg></span>
            <h3>Safety, without compromise</h3>
            <p>Vetted drivers, speed-governed coaches and pre-departure inspections. Nothing leaves the yard unchecked.</p>
          </article>
          <article class="value-card value-fares">
            <span class="value-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2v20"></path><path d="M17 6.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 2.8 5 3.3 5 1.4 5 3.3-2.2 3-5 3-5-1.1-5-3"></path></svg></span>
            <h3>Fares that stay fair</h3>
            <p>One clear price with no hidden add-ons at checkout — the fare you see is the fare you pay.</p>
          </article>
          <article class="value-card value-time">
            <span class="value-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 7v5l3 2"></path><circle cx="12" cy="12" r="9"></circle></svg></span>
            <h3>On time, as promised</h3>
            <p>Punctuality is the discipline the whole company is built around — departures you can plan a day around.</p>
          </article>
        </div>
      </div>
    </section>

    <section class="about-section">
      <div class="about-container fleet-grid">
        <div class="fleet-copy">
          <span class="section-kicker">Fleet &amp; safety</span>
          <h2>Comfort is designed in. Safety is checked in.</h2>
          <p class="section-intro">Every {{ operatorName }} coach is maintained to a strict operating schedule, with the features passengers need for an easier journey.</p>
          <ul>
            @for (point of fleetPoints; track point) {
              <li><span>✓</span>{{ point }}</li>
            }
          </ul>
        </div>
        <img
          class="fleet-image"
          [src]="about.fleetImage || 'assets/images/about/mash-poa-premium-interior.jpeg'"
          [alt]="operatorName + ' premium bus interior, passenger seating and crew member'"
          loading="lazy">
      </div>
    </section>

    <section class="about-section about-network">
      <div class="about-container">
        <div class="section-heading">
          <span class="section-kicker">Our network</span>
          <h2>Connecting the region</h2>
          <p>From the Kenyan coast to neighbouring capitals, one booking gets you moving.</p>
        </div>
        <div class="network-grid">
          @for (destination of destinations; track destination.city) {
            <a class="destination-card" routerLink="/" fragment="search">
              <img [src]="destination.image" [alt]="destination.city">
              <span class="destination-shade"></span>
              <span class="destination-copy">
                <small>{{ destination.country }}</small>
                <strong>{{ destination.city }}</strong>
                <em>{{ destination.note }}</em>
              </span>
            </a>
          }
        </div>
      </div>
    </section>

    <section class="about-cta">
      <div class="about-container cta-inner">
        <div>
          <span>Travel with {{ operatorName }}</span>
          <h2>Ready when you are.</h2>
          <p>Choose your destination, pick your seat and travel with confidence.</p>
        </div>
        <div class="about-actions">
          <a class="about-btn about-btn-light" routerLink="/" fragment="search">Book a bus</a>
          <a class="about-btn about-btn-outline-light" routerLink="/parcel">Send a parcel</a>
        </div>
      </div>
    </section>
  </main>

</div>
